import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { IconPencil } from "../icons";

const editIdleClass =
  "cursor-text rounded px-1 py-0.5 transition-colors hover:bg-zinc-100";

const editInputClass =
  "rounded border border-brand-300 bg-white px-1.5 py-0.5 font-inherit text-inherit outline-none ring-2 ring-brand-500/20";

const editPlainInputClass =
  "border-0 bg-transparent px-1 py-0.5 font-inherit text-inherit outline-none ring-0 shadow-none";

interface InlineEditableProps {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  plainEdit?: boolean;
  showEditIcon?: boolean;
  className?: string;
  inputClassName?: string;
  emptyClassName?: string;
  startIcon?: ReactNode;
  maxLength?: number;
  as?: "span" | "p" | "h1" | "h2";
}

export function InlineEditable({
  value,
  placeholder,
  onChange,
  multiline = false,
  plainEdit = false,
  showEditIcon = true,
  className = "",
  inputClassName = "",
  emptyClassName = "text-slate-400",
  startIcon,
  maxLength,
  as: Tag = "span",
}: InlineEditableProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (!editing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing]);

  const commit = () => {
    onChange(draft);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const startEditing = () => {
    setEditing(true);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (
      event.key === "Enter" &&
      (!multiline || event.metaKey || event.ctrlKey)
    ) {
      event.preventDefault();
      commit();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      cancel();
    }
  };

  if (!editing) {
    const display = value.trim() ? value : placeholder;
    const isEmpty = !value.trim();
    const textClassName = `${isEmpty ? emptyClassName : className}`.trim();

    return (
      <Tag
        role="button"
        tabIndex={0}
        onClick={startEditing}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            startEditing();
          }
        }}
        className={`group inline-flex max-w-full items-center gap-1 ${editIdleClass}`.trim()}
      >
        {startIcon ? (
          <span className="shrink-0 text-slate-400 transition-colors group-hover:text-slate-500">
            {startIcon}
          </span>
        ) : null}
        <span className={textClassName || undefined}>{display}</span>
        {showEditIcon ? (
          <IconPencil
            size={11}
            className="mb-px shrink-0 text-slate-400/80 transition-colors group-hover:text-slate-500"
            aria-hidden
          />
        ) : null}
      </Tag>
    );
  }

  const activeEditClass = plainEdit ? editPlainInputClass : editInputClass;

  if (multiline) {
    return (
      <textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        rows={2}
        maxLength={maxLength}
        value={draft}
        onChange={(event) =>
          setDraft(
            maxLength
              ? event.target.value.slice(0, maxLength)
              : event.target.value,
          )
        }
        onBlur={commit}
        onKeyDown={handleKeyDown}
        className={`${activeEditClass} block w-full resize-y ${inputClassName}`.trim()}
      />
    );
  }

  return (
    <input
      ref={inputRef as React.RefObject<HTMLInputElement>}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={handleKeyDown}
      className={`${activeEditClass} ${inputClassName}`.trim()}
    />
  );
}

export function InlineEditableLabel({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`mb-1 block font-onest text-xs font-medium text-slate-500 ${className}`.trim()}
    >
      {children}
    </span>
  );
}
